import { AxiosResponse } from 'axios';
import { ApiResponse } from '../types/api';
import { API_ORIGIN } from '../config';

/**
 * API 기본 URL
 */
export const API_URL = API_ORIGIN;

/** GET /api/pools, /api/users 등 다양한 응답 형태에서 배열 추출 */
export function extractArrayFromApiPayload(raw: unknown): unknown[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  for (const key of ['data', 'pools', 'users', 'admins', 'items', 'backups']) {
    const v = o[key];
    if (Array.isArray(v)) return v;
  }
  if (o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
    return extractArrayFromApiPayload(o.data);
  }
  return [];
}

/** @deprecated extractArrayFromApiPayload 사용 */
export function extractPoolsList(raw: unknown): unknown[] {
  return extractArrayFromApiPayload(raw);
}

/**
 * API 응답을 처리하여 표준화된 형태로 반환합니다.
 * @param response Axios 응답 객체
 * @returns 표준화된 API 응답
 */
export function handleApiResponse<T>(response: AxiosResponse): ApiResponse<T> {
  const { data, status } = response;

  console.log(`[API 응답 처리] 상태 코드: ${status}, 데이터:`, JSON.stringify(data, null, 2));

  if (data && data.hasOwnProperty('success')) {
    console.log('[API 응답 처리] 서버에서 ApiResponse 형식으로 응답 받음, success:', data.success);
    return data as ApiResponse<T>;
  }

  console.log('[API 응답 처리] 일반 데이터 응답을 ApiResponse 형식으로 변환');
  let payload: T = data as T;
  if (
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Array.isArray((data as { pools?: unknown[] }).pools)
  ) {
    payload = (data as { pools: T }).pools;
  }

  return {
    success: true,
    data: payload,
    status: status,
    message: '요청 성공',
  };
}

/**
 * API 오류를 로깅하고 표준화된 오류 응답을 반환합니다.
 * @param message 오류 메시지 접두사
 * @param error 발생한 오류 객체
 * @returns 표준화된 API 오류 응답
 */
export function logApiError<T>(message: string, error: any): ApiResponse<T> {
  console.error(`[API 오류] ${message}:`, error);
  
  // 서버에서 반환한 오류 응답이 있는 경우
  if (error.response) {
    const { status, data } = error.response;
    console.error(`[API 오류 응답] 상태 코드: ${status}, 데이터:`, JSON.stringify(data, null, 2));
    
    // 서버에서 이미 ApiResponse 형태로 오류를 반환한 경우
    if (data && data.hasOwnProperty('success') && data.success === false) {
      console.error('[API 오류 응답] 서버에서 ApiResponse 형식으로 오류 응답 받음');
      return data as ApiResponse<T>;
    }
    
    // 서버에서 일반 오류 메시지만 반환한 경우
    console.error('[API 오류 응답] 일반 오류 응답을 ApiResponse 형식으로 변환');
    return {
      success: false,
      data: null,
      message: data.message || data.error || data.detail || String(data),
      status: status
    };
  }
  
  // 네트워크 오류 등 서버 응답이 없는 경우
  console.error('[API 오류] 서버 응답 없음 (네트워크 오류 등)');
  return {
    success: false,
    data: null,
    message: error.message || '알 수 없는 오류',
    status: 500
  };
} 